import React from "react";
import LegalPage from "../../components/LegalPage";

export default function PrivacyPolicy() {
  return (
    <LegalPage
      testid="legal-privacy"
      eyebrow="BlueRoam Ltd · Company No. 17215030 · 128 City Road, London, EC1V 2NX"
      title="Privacy Policy"
      lastUpdated="14 May 2026 · Version 1.1 (Global)"
      intro="BlueRoam Ltd (“BlueRoam”, “we”, “us”, “our”) is committed to protecting your personal data. This Privacy Policy explains what personal data we collect about you, how we use it, who we share it with and what your rights are under applicable privacy laws worldwide."
    >
      <p>
        This Policy applies to customers globally. Jurisdiction-specific rights and disclosures are
        provided in Section 10 (UK, EU/EEA, Switzerland), Section 11 (California and other US
        states), Section 12 (Canada), Section 13 (Brazil — LGPD), Section 14 (Australia) and
        Section 15 (Other jurisdictions).
      </p>

      <h2>1. Who We Are (Data Controller)</h2>
      <p>
        BlueRoam Ltd is the <strong>data controller</strong> (or “business” for US state laws, or
        “data fiduciary” / equivalent term in other jurisdictions) for your personal data.
      </p>
      <ul>
        <li><strong>Registered office:</strong> 128 City Road, London, EC1V 2NX, United Kingdom</li>
        <li><strong>Company number:</strong> 17215030</li>
        <li><strong>Privacy contact:</strong> <a href="mailto:hello@blueroam.io">hello@blueroam.io</a></li>
        <li><strong>EU Representative (Art. 27 EU GDPR):</strong> Panos Vlahos, Patron 4, 4044, Limassol, Cyprus</li>
        <li><strong>UK Data Protection Officer / Privacy Lead:</strong> Andrew Folson, Elpidas 14, 4529, Pyrgos Limassol, Cyprus (or via <a href="mailto:hello@blueroam.io">hello@blueroam.io</a>)</li>
      </ul>

      <h2>2. Laws That Apply</h2>
      <p>Depending on where you are located when your data is processed, one or more of the following laws may apply:</p>
      <ul>
        <li><strong>UK GDPR</strong> and the Data Protection Act 2018;</li>
        <li><strong>EU GDPR</strong> (Regulation 2016/679);</li>
        <li><strong>Swiss Federal Act on Data Protection</strong> (revFADP);</li>
        <li><strong>US state laws</strong> including CCPA/CPRA (California), VCDPA (Virginia), CPA (Colorado), CTDPA (Connecticut), UCPA (Utah), TDPSA (Texas), OCPA (Oregon) and other applicable state laws;</li>
        <li><strong>Brazil LGPD</strong>;</li>
        <li><strong>Canada PIPEDA</strong> (and Quebec Law 25);</li>
        <li><strong>Australia Privacy Act 1988</strong>;</li>
        <li>Other applicable national or sub-national data-protection laws.</li>
      </ul>

      <h2>3. Scope</h2>
      <p>This Policy applies to personal data we collect:</p>
      <ul>
        <li>when you visit <strong>blueroam.io</strong> or use our applications;</li>
        <li>when you create an account or purchase an eSIM Data Plan;</li>
        <li>when you contact us (support, complaints, sales);</li>
        <li>when you interact with our marketing communications;</li>
        <li>from third-party sources (payment processors, fraud-prevention partners, identity verification providers, Partner Network operators).</li>
      </ul>

      <h2>4. Personal Data We Collect</h2>
      <div className="overflow-x-auto my-4">
        <table className="w-full text-sm border border-ink-200 rounded-xl overflow-hidden">
          <thead className="bg-ink-50">
            <tr><th className="text-left p-3 border-b border-ink-200">Category</th><th className="text-left p-3 border-b border-ink-200">Examples</th></tr>
          </thead>
          <tbody>
            <tr><td className="p-3 border-b border-ink-200"><strong>Identity data</strong></td><td className="p-3 border-b border-ink-200">Name, date of birth (where required for SIM registration), nationality (where required)</td></tr>
            <tr><td className="p-3 border-b border-ink-200"><strong>Contact data</strong></td><td className="p-3 border-b border-ink-200">Email, mobile, postal address, billing address</td></tr>
            <tr><td className="p-3 border-b border-ink-200"><strong>Account data</strong></td><td className="p-3 border-b border-ink-200">Username, hashed password, purchase history, support tickets, preferences</td></tr>
            <tr><td className="p-3 border-b border-ink-200"><strong>Transaction data</strong></td><td className="p-3 border-b border-ink-200">Order details, Data Plans purchased, amounts paid, currency, taxes, refunds</td></tr>
            <tr><td className="p-3 border-b border-ink-200"><strong>Payment data</strong></td><td className="p-3 border-b border-ink-200">Limited card metadata (last 4, type, expiry); full card details are processed by our payment service providers and are not stored by BlueRoam</td></tr>
            <tr><td className="p-3 border-b border-ink-200"><strong>Device &amp; technical</strong></td><td className="p-3 border-b border-ink-200">Device model, OS, browser, IP address, eSIM identifiers (EID, ICCID) where necessary, device fingerprint</td></tr>
            <tr><td className="p-3 border-b border-ink-200"><strong>Location data</strong></td><td className="p-3 border-b border-ink-200">Approximate location from IP; country/region of eSIM use</td></tr>
            <tr><td className="p-3 border-b border-ink-200"><strong>Usage data</strong></td><td className="p-3 border-b border-ink-200">Pages visited, features used, clicks, session duration, referrers</td></tr>
            <tr><td className="p-3 border-b border-ink-200"><strong>Marketing data</strong></td><td className="p-3 border-b border-ink-200">Preferences, engagement with our communications</td></tr>
            <tr><td className="p-3 border-b border-ink-200"><strong>Identity verification</strong></td><td className="p-3 border-b border-ink-200">(Where EDD or country-specific SIM registration applies) passport/ID images, selfie or video, document metadata</td></tr>
            <tr><td className="p-3 border-b border-ink-200"><strong>Communications</strong></td><td className="p-3 border-b border-ink-200">Records of correspondence, support chats, call recordings (where notified)</td></tr>
            <tr><td className="p-3"><strong>Fraud prevention</strong></td><td className="p-3">Risk scores, sanctions/PEP screening results, chargeback history</td></tr>
          </tbody>
        </table>
      </div>
      <p>
        We do <strong>not</strong> intentionally collect “special category” / “sensitive” personal
        data unless required by law (e.g., from identity documents that incidentally reveal it, in
        which case we process the minimum necessary). We do <strong>not</strong> knowingly collect
        data from children under 18 (under 13 in the US — see §11).
      </p>

      <h2>5. How We Use Your Personal Data (Purposes and Legal Bases)</h2>
      <p>We process personal data on the following legal bases under Article 6 UK/EU GDPR (and equivalent bases under other laws):</p>
      <div className="overflow-x-auto my-4">
        <table className="w-full text-sm border border-ink-200 rounded-xl overflow-hidden">
          <thead className="bg-ink-50"><tr><th className="text-left p-3 border-b border-ink-200">Purpose</th><th className="text-left p-3 border-b border-ink-200">Legal basis</th></tr></thead>
          <tbody>
            <tr><td className="p-3 border-b border-ink-200">Creating and managing your account</td><td className="p-3 border-b border-ink-200">Performance of a contract</td></tr>
            <tr><td className="p-3 border-b border-ink-200">Processing your order and delivering your eSIM</td><td className="p-3 border-b border-ink-200">Performance of a contract</td></tr>
            <tr><td className="p-3 border-b border-ink-200">Providing customer support</td><td className="p-3 border-b border-ink-200">Contract; Legitimate interests</td></tr>
            <tr><td className="p-3 border-b border-ink-200">Processing payments and refunds</td><td className="p-3 border-b border-ink-200">Contract; Legal obligation</td></tr>
            <tr><td className="p-3 border-b border-ink-200">Detecting and preventing fraud, abuse, unauthorised use</td><td className="p-3 border-b border-ink-200">Legitimate interests; Legal obligation</td></tr>
            <tr><td className="p-3 border-b border-ink-200">Sanctions, PEP and AML screening</td><td className="p-3 border-b border-ink-200">Legal obligation; Legitimate interests</td></tr>
            <tr><td className="p-3 border-b border-ink-200">Country-specific eSIM/SIM registration</td><td className="p-3 border-b border-ink-200">Legal obligation (destination country); Contract</td></tr>
            <tr><td className="p-3 border-b border-ink-200">Transactional emails (order confirmations, service notices)</td><td className="p-3 border-b border-ink-200">Contract</td></tr>
            <tr><td className="p-3 border-b border-ink-200">Direct marketing to existing customers (soft opt-in / similar products)</td><td className="p-3 border-b border-ink-200">Legitimate interests, subject to PECR and equivalent ePrivacy rules</td></tr>
            <tr><td className="p-3 border-b border-ink-200">Direct marketing to prospects</td><td className="p-3 border-b border-ink-200">Consent</td></tr>
            <tr><td className="p-3 border-b border-ink-200">Analytics and service improvement</td><td className="p-3 border-b border-ink-200">Legitimate interests</td></tr>
            <tr><td className="p-3 border-b border-ink-200">Personalisation and targeted advertising</td><td className="p-3 border-b border-ink-200">Consent (where required)</td></tr>
            <tr><td className="p-3 border-b border-ink-200">Legal and regulatory compliance</td><td className="p-3 border-b border-ink-200">Legal obligation</td></tr>
            <tr><td className="p-3 border-b border-ink-200">Legal claims</td><td className="p-3 border-b border-ink-200">Legitimate interests; Legal obligation</td></tr>
            <tr><td className="p-3">Corporate transactions (M&amp;A, restructuring)</td><td className="p-3">Legitimate interests</td></tr>
          </tbody>
        </table>
      </div>
      <p>
        Where we rely on <strong>legitimate interests</strong>, we have carried out and documented a
        balancing test. Where we rely on <strong>consent</strong>, you may withdraw it at any time
        without affecting the lawfulness of prior processing.
      </p>

      <h2>6. Cookies and Similar Technologies</h2>
      <p>
        Our use of cookies is governed by our Cookie Notice at <strong>blueroam.io/cookies</strong>.
        Non-essential cookies are set only with your consent in line with the EU/UK ePrivacy
        regimes, the Quebec Law 25 cookie rules and other applicable laws. US users in CPRA states
        have the right to opt out of “sharing” / “sale” of personal data through cookies (see §11).
      </p>

      <h2>7. Marketing</h2>
      <p>
        7.1 We send marketing communications where you have consented or where we are permitted under
        applicable law (e.g., the UK PECR “soft opt-in” for existing customers; CAN-SPAM in the US;
        CASL in Canada with express or implied consent).
      </p>
      <p>
        7.2 Opt out at any time via “unsubscribe” in any marketing email, account preferences, or{" "}
        <a href="mailto:hello@blueroam.io">hello@blueroam.io</a>. Opt-outs do not affect
        transactional or service-related communications.
      </p>

      <h2>8. Disclosure to Third Parties</h2>
      <p>We share personal data with:</p>
      <ul>
        <li><strong>Partner Networks and eSIM platform providers</strong> (to provision and manage your eSIM);</li>
        <li><strong>Payment service providers</strong> (e.g., NeptunePay);</li>
        <li><strong>Identity verification and fraud-prevention providers</strong> where EDD or AML screening applies;</li>
        <li><strong>Cloud hosting and IT infrastructure providers</strong> (e.g., AWS);</li>
        <li><strong>Communications providers</strong> (email, SMS, customer support);</li>
        <li><strong>Analytics providers</strong> (e.g., Google Analytics, etc.), subject to your cookie preferences;</li>
        <li><strong>Professional advisers</strong> (lawyers, accountants, auditors) under duties of confidentiality;</li>
        <li><strong>Regulators, law enforcement and other authorities</strong> where required by law or to protect our rights or others’;</li>
        <li><strong>Acquirers, investors or successors</strong> in corporate transactions.</li>
      </ul>
      <p>
        We require all processors to act only on documented instructions and maintain appropriate
        security and confidentiality (UK/EU GDPR Art. 28 and equivalent).
      </p>
      <p>
        We do <strong>not sell</strong> personal data in the ordinary commercial sense. For the
        specific definitions of “sell” and “share” under California and other US state laws, see §11
        and our US Privacy Notice.
      </p>

      <h2>9. International Data Transfers</h2>
      <p>
        9.1 BlueRoam is based in the UK. Where personal data is transferred to or from countries
        that do not have an adequacy decision, we use one of the following safeguards:
      </p>
      <ul>
        <li>The <strong>UK International Data Transfer Agreement (IDTA)</strong> or the UK Addendum to the EU Standard Contractual Clauses;</li>
        <li>The <strong>EU Standard Contractual Clauses (2021)</strong>;</li>
        <li>A recognised adequacy regulation/decision;</li>
        <li>Another lawful transfer mechanism under Article 46 or 49 UK/EU GDPR.</li>
      </ul>
      <p>9.2 Data may also be subject to onward transfer by Partner Networks and payment providers, in each case under their own data-protection regimes.</p>
      <p>9.3 You can request copies of the safeguards via <a href="mailto:hello@blueroam.io">hello@blueroam.io</a>.</p>

      <h2>10. UK, EU/EEA and Swiss Residents — Your Rights Under UK GDPR / EU GDPR / revFADP</h2>
      <p>Subject to applicable law and exemptions, you have the right to:</p>
      <ul>
        <li><strong>Access</strong> your personal data;</li>
        <li><strong>Rectify</strong> inaccurate or incomplete data;</li>
        <li><strong>Erase</strong> your data (“right to be forgotten”);</li>
        <li><strong>Restrict</strong> processing in certain circumstances;</li>
        <li><strong>Object</strong> to processing based on legitimate interests or for direct marketing;</li>
        <li><strong>Data portability</strong> for data processed on the basis of consent or contract;</li>
        <li><strong>Withdraw consent</strong> where processing is based on consent;</li>
        <li><strong>Not be subject</strong> to decisions based solely on automated processing producing legal or similarly significant effects (see §16);</li>
        <li><strong>Lodge a complaint</strong> with your local supervisory authority (e.g., the UK ICO at ico.org.uk; in the EU, your national DPA — list at edpb.europa.eu).</li>
      </ul>
      <p>
        To exercise these rights: <a href="mailto:hello@blueroam.io">hello@blueroam.io</a>. We
        respond within <strong>one month</strong> (extendable by two further months in complex
        cases). We may need to verify your identity.
      </p>
      <p>
        EU/EEA residents may also contact our <strong>EU Representative</strong> at Panos Vlahos,
        Patron 4, 4044, Limassol, Cyprus (or via <a href="mailto:hello@blueroam.io">hello@blueroam.io</a>).
      </p>

      <h2>11. California and Other US State-Law Residents</h2>
      <p>
        If you are a resident of California, Virginia, Colorado, Connecticut, Utah, Texas, Oregon,
        or any other US state with a comprehensive consumer privacy law that applies to BlueRoam,
        you have additional rights described in our US Privacy Notice. In summary:
      </p>
      <ul>
        <li><strong>Right to know</strong> what personal information we collect, use, disclose and (if applicable) sell or share;</li>
        <li><strong>Right to delete</strong> personal information we hold about you;</li>
        <li><strong>Right to correct</strong> inaccurate personal information;</li>
        <li><strong>Right to opt out</strong> of “sale” or “sharing” of personal information for cross-context behavioural advertising;</li>
        <li><strong>Right to limit</strong> use of “sensitive personal information” (California);</li>
        <li><strong>Right to data portability</strong>;</li>
        <li><strong>Right to non-discrimination</strong> for exercising your rights;</li>
        <li>(Colorado, Connecticut, Texas, Oregon and some others) <strong>Right to opt out of profiling</strong> for legally or similarly significant decisions;</li>
        <li><strong>Right to appeal</strong> denials of your requests (Virginia, Colorado, Connecticut, Texas, Oregon).</li>
      </ul>
      <p>
        To exercise these rights, see the US Privacy Notice or email{" "}
        <a href="mailto:hello@blueroam.io">hello@blueroam.io</a>.
      </p>
      <p>
        <strong>BlueRoam does not sell personal information for money.</strong> We may “share”
        personal information for cross-context behavioural advertising in the CPRA sense; if you
        wish to opt out, see the “Do Not Sell or Share My Personal Information” link in our website
        footer and our US Privacy Notice.
      </p>

      <h2>12. Canada (PIPEDA, Quebec Law 25)</h2>
      <p>
        If you are in Canada, you have rights to access, correct and challenge our handling of your
        personal information under PIPEDA and provincial laws (including Quebec Law 25). You may
        also complain to the Office of the Privacy Commissioner of Canada at priv.gc.ca or to your
        provincial commissioner. Quebec residents have specific rights including data portability
        and rights regarding automated decision-making (see §16).
      </p>

      <h2>13. Brazil (LGPD)</h2>
      <p>
        If you are in Brazil, you have rights under the Lei Geral de Proteção de Dados (LGPD)
        including access, correction, anonymisation/blocking/deletion, portability, information
        about sharing, withdrawal of consent and review of automated decisions. Complaints may be
        lodged with the Brazilian National Data Protection Authority (ANPD).
      </p>

      <h2>14. Australia</h2>
      <p>
        If you are in Australia, you have rights under the Privacy Act 1988 and the Australian
        Privacy Principles, including access and correction rights. Complaints may be lodged with
        the Office of the Australian Information Commissioner at oaic.gov.au.
      </p>

      <h2>15. Other Jurisdictions</h2>
      <p>
        If you are in a country not specifically named above, we comply with applicable local
        data-protection laws. You may contact{" "}
        <a href="mailto:hello@blueroam.io">hello@blueroam.io</a> to exercise rights available under
        those laws.
      </p>

      <h2>16. Automated Decision-Making</h2>
      <p>
        Some elements of our fraud, sanctions and risk-screening processes involve automated
        decisions (e.g., automatic decline of a high-risk transaction). Where such a decision
        produces legal or similarly significant effects for you, we will, on request:
      </p>
      <ul>
        <li>inform you of the decision;</li>
        <li>allow you to obtain human intervention;</li>
        <li>allow you to express your point of view; and</li>
        <li>allow you to contest the decision.</li>
      </ul>
      <p>Contact <a href="mailto:hello@blueroam.io">hello@blueroam.io</a>.</p>

      <h2>17. Data Retention</h2>
      <div className="overflow-x-auto my-4">
        <table className="w-full text-sm border border-ink-200 rounded-xl overflow-hidden">
          <thead className="bg-ink-50"><tr><th className="text-left p-3 border-b border-ink-200">Data</th><th className="text-left p-3 border-b border-ink-200">Retention</th></tr></thead>
          <tbody>
            <tr><td className="p-3 border-b border-ink-200">Account and order data</td><td className="p-3 border-b border-ink-200">Duration of account + <strong>6 years</strong></td></tr>
            <tr><td className="p-3 border-b border-ink-200">Payment and transaction records</td><td className="p-3 border-b border-ink-200"><strong>6 years</strong> (HMRC / equivalent tax-record obligations)</td></tr>
            <tr><td className="p-3 border-b border-ink-200">AML, sanctions, identity-verification records</td><td className="p-3 border-b border-ink-200"><strong>5 years</strong> from end of relationship or transaction</td></tr>
            <tr><td className="p-3 border-b border-ink-200">Marketing preferences</td><td className="p-3 border-b border-ink-200">Until you opt out or <strong>24 months</strong> of inactivity</td></tr>
            <tr><td className="p-3 border-b border-ink-200">Cookies and analytics</td><td className="p-3 border-b border-ink-200">As set out in the Cookie Notice (typically up to <strong>24 months</strong>)</td></tr>
            <tr><td className="p-3">Customer support correspondence</td><td className="p-3"><strong>3 years</strong> from closure</td></tr>
          </tbody>
        </table>
      </div>
      <p>After retention periods, data is securely deleted or anonymised, save where retention is required by law.</p>

      <h2>18. Security</h2>
      <p>
        We implement appropriate technical and organisational measures including encryption in
        transit and at rest, access controls, network security, pseudonymisation, regular testing,
        incident response and staff training. No system is completely secure; we notify you and/or
        competent authorities of breaches as required by law (within 72 hours under UK/EU GDPR;
        under varying timeframes for US states; without undue delay under LGPD, etc.).
      </p>

      <h2>19. Children</h2>
      <p>
        Our Services are not directed at, and we do not knowingly collect data from, individuals
        under <strong>18</strong>. In the US, we comply with the Children’s Online Privacy
        Protection Act (COPPA) and do not knowingly collect data from children under{" "}
        <strong>13</strong>. If you believe a child has provided personal data, contact{" "}
        <a href="mailto:hello@blueroam.io">hello@blueroam.io</a> and we will take appropriate steps.
      </p>

      <h2>20. Changes to This Policy</h2>
      <p>
        We may update this Policy. The “Effective date” shows when the latest version took effect.
        Material changes are notified by email or via the Platform with reasonable notice.
      </p>

      <h2>21. Contact</h2>
      <ul>
        <li><strong>Email:</strong> <a href="mailto:hello@blueroam.io">hello@blueroam.io</a></li>
        <li><strong>Post:</strong> Data Protection, BlueRoam Ltd, 128 City Road, London, EC1V 2NX, United Kingdom</li>
        <li><strong>EU Representative (Art. 27 GDPR):</strong> Panos Vlahos, Patron 4, 4044, Limassol, Cyprus (or via <a href="mailto:hello@blueroam.io">hello@blueroam.io</a>)</li>
        <li><strong>UK ICO:</strong> ico.org.uk | 0303 123 1113</li>
        <li><strong>EU DPAs:</strong> edpb.europa.eu</li>
        <li><strong>California Attorney General:</strong> oag.ca.gov/privacy</li>
      </ul>
    </LegalPage>
  );
}
